import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import cn from 'classnames';
import { Badge, Divider, Row, Tooltip, Typography } from 'seagull';
import { Edit } from 'seagull/icons';
import { VariableInlineRenderer } from '~/components/AgreementStudioPage/TemplateVariables/VariableInlineRenderer/VariableInlineRenderer';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { serviceModel } from '~/models';
import { billingBadgesTextsPath, discountBadge } from '~/utils/service-description/getServiceDescriptionBadges';
import IncludedServices from '../IncludedServices/IncludedServices';
import BadgesList from './BadgesList/BadgesList';
import BulletPointsSection from './BulletPointsSection/BulletPointsSection';
import EditablePrice from './EditablePrice/EditablePrice';
import PriceDisplay from './PriceDisplay/PriceDisplay';
import { ServiceBillingInlineSettings } from './ServiceBillingInlineSettings/ServiceBillingInlineSettings';
import ServiceDescription from './ServiceDescription/ServiceDescription';
import styles from './ServiceDetails.module.scss';
import { getPausedBadge, getPriceDisplayProps, getStatusBadgeText } from './ServiceDetails.utils';
export default function ServiceDetails(props) {
    var _service_bundle;
    const { testId, menu, agreementData, showBadges, showBulletPoints, billingInitiationProps, isFreshlyAdded = false, additionalSections, isVendor, disableEditBillUpfront, disableEditBillingDay, disableStatusBadge = false, disableEditCost, disableEditBillingInitiation, isReadOnly, ignoreAmendments, error, serviceOrigin, relationshipState, onCostChange, onHasError, onDayOfMonthChange, onDayOfWeekChange, onBillUpfrontModeChange, onChangeService, onDayOfYearChange, onIncludedServicesChange, onPausedChange, onBillOnAcceptanceChange, onStartBillingAtChange, context, onEditServiceNameAndDescription } = props;
    const { SERVICE_TEMPLATE_BUNDLE, INLINE_EDIT_SERVICE_NAME_DESCRIPTION = false } = useFeatureFlags();
    const { t: billingBadgesTexts } = useLocaleKeys(billingBadgesTextsPath);
    const service = ignoreAmendments ? props.service : serviceModel.applyChanges(props.service);
    const isActiveAgreement = agreementData.isActiveAgreement();
    const statusBadgeText = getStatusBadgeText(service, isActiveAgreement, disableStatusBadge);
    const pausedBadge = getPausedBadge(service, isVendor, isActiveAgreement);
    const discountBadgeData = discountBadge(service, billingBadgesTexts);
    const priceDisplayProps = getPriceDisplayProps(service, agreementData);
    const isBundle = Boolean(SERVICE_TEMPLATE_BUNDLE && serviceModel.isBundle(service));
    const enableEditServiceNameAndDescription = INLINE_EDIT_SERVICE_NAME_DESCRIPTION && !!onEditServiceNameAndDescription && !isReadOnly;
    const showReadMoreButton = useMemo(()=>!enableEditServiceNameAndDescription || !INLINE_EDIT_SERVICE_NAME_DESCRIPTION, [
        enableEditServiceNameAndDescription,
        INLINE_EDIT_SERVICE_NAME_DESCRIPTION
    ]);
    const isEditMode = !!relationshipState;
    const showPausedBadge = !(isEditMode && !isReadOnly);
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.serviceDetails,
        "data-testid": "".concat(testId, "service-details"),
        "data-serviceid": service.id,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles.descriptionAndMenuButtonContainer,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: cn(styles.detailedDescription, {
                            [styles.withBadge]: !!statusBadgeText
                        }),
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: cn(styles.serviceNameAndDescriptionContainer, {
                                [styles.clickable]: enableEditServiceNameAndDescription
                            }),
                            onClick: enableEditServiceNameAndDescription ? ()=>onEditServiceNameAndDescription(service) : undefined,
                            children: [
                                /*#__PURE__*/ _jsxs(Row, {
                                    alignItems: "center",
                                    className: styles.serviceNameRow,
                                    children: [
                                        /*#__PURE__*/ _jsx(Typography, {
                                            variant: "h5",
                                            ellipsis: true,
                                            testId: testId + 'name',
                                            children: /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                                                text: service.name,
                                                mode: "text"
                                            })
                                        }),
                                        enableEditServiceNameAndDescription && /*#__PURE__*/ _jsx(Row, {
                                            className: styles.editIconButton,
                                            testId: testId + 'inline-edit',
                                            children: /*#__PURE__*/ _jsx(Edit, {})
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: styles.serviceDescriptionContainer,
                                    children: /*#__PURE__*/ _jsx(ServiceDescription, {
                                        service: service,
                                        testId: testId,
                                        showReadMoreButton: showReadMoreButton
                                    })
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles.badgeAndDotMenuContainer,
                        children: [
                            pausedBadge && showPausedBadge && /*#__PURE__*/ _jsx(Tooltip, {
                                title: pausedBadge.tooltip,
                                placement: "top",
                                children: /*#__PURE__*/ _jsx(Badge, {
                                    outlined: true,
                                    testId: "service-details-paused-badge",
                                    children: "Paused"
                                })
                            }),
                            statusBadgeText && /*#__PURE__*/ _jsx(Badge, {
                                outlined: true,
                                testId: "".concat(testId, "status-badge"),
                                children: statusBadgeText
                            }),
                            menu
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.finalPriceContainer,
                children: [
                    onCostChange && onChangeService ? /*#__PURE__*/ _jsx(EditablePrice, {
                        agreementData: agreementData,
                        serviceOrigin: serviceOrigin,
                        serviceWithChanges: service,
                        service: props.service,
                        testId: testId,
                        disableEditCost: disableEditCost,
                        isReadOnly: isReadOnly,
                        showDiscountError: !!priceDisplayProps.error,
                        onCostChange: onCostChange,
                        onChangeService: onChangeService
                    }) : /*#__PURE__*/ _jsx(PriceDisplay, _object_spread_props(_object_spread({}, priceDisplayProps), {
                        testId: testId
                    })),
                    showBadges ? /*#__PURE__*/ _jsx(BadgesList, {
                        service: service,
                        agreementData: agreementData,
                        testId: testId,
                        isFreshlyAdded: isFreshlyAdded,
                        context: context,
                        isReadOnly: isReadOnly
                    }) : discountBadgeData && /*#__PURE__*/ _jsx("div", {
                        "data-testid": testId + 'discount',
                        children: /*#__PURE__*/ _jsx(Badge, {
                            skin: discountBadgeData.skin,
                            children: discountBadgeData.text
                        })
                    })
                ]
            }),
            isBundle && /*#__PURE__*/ _jsxs("div", {
                className: styles.includedServicesContainer,
                children: [
                    isReadOnly && /*#__PURE__*/ _jsx(Divider, {
                        margin: 12
                    }),
                    /*#__PURE__*/ _jsx(IncludedServices, {
                        showErrors: error,
                        mode: isReadOnly ? 'readonly' : 'editAndDelete',
                        value: ((_service_bundle = service.bundle) === null || _service_bundle === void 0 ? void 0 : _service_bundle.services) || [],
                        onChange: (value)=>onIncludedServicesChange && onIncludedServicesChange(value),
                        tooltip: "Included service rates are hidden from the client"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ServiceBillingInlineSettings, {
                service: service,
                testId: testId,
                agreementData: agreementData,
                billingInitiation: billingInitiationProps,
                relationshipState: relationshipState,
                isFreshlyAdded: isFreshlyAdded,
                showBillingSettingsSection: !showBulletPoints,
                disableBillingInitiation: disableEditBillingInitiation,
                disableBillUpfront: disableEditBillUpfront,
                disableBillingDay: disableEditBillingDay,
                showError: error,
                billingSettingsClassName: styles.section,
                isReadOnly: isReadOnly,
                onChangeService: onChangeService,
                onDayOfMonthChange: onDayOfMonthChange,
                onDayOfWeekChange: onDayOfWeekChange,
                onDayOfYearChange: onDayOfYearChange,
                onBillUpfrontModeChange: onBillUpfrontModeChange,
                onHasError: onHasError,
                onPausedChange: onPausedChange,
                onBillOnAcceptanceChange: onBillOnAcceptanceChange,
                onStartBillingAtChange: onStartBillingAtChange
            }),
            showBulletPoints && /*#__PURE__*/ _jsx(BulletPointsSection, _object_spread({}, props)),
            additionalSections && additionalSections.length > 0 && /*#__PURE__*/ _jsx(Divider, {}),
            additionalSections === null || additionalSections === void 0 ? void 0 : additionalSections.map((param, idx)=>{
                let { value, disableBorder } = param;
                return value && /*#__PURE__*/ _jsx("div", {
                    className: cn(styles.additionalSection, disableBorder && styles.disableBorder),
                    children: value
                }, idx);
            })
        ]
    });
}
